import { useState, useRef, useEffect, useCallback } from 'react'
import { lib } from '@gocanvas/client'
const { isNumber } = lib

export const useInactiveUpdate = (
  initialValue,
  validate,
  update,
  {
    delay = 500,
    min,
    max,
  }) => {
  const cleanInitialValue = initialValue === null || initialValue === undefined
    ? ''
    : String(initialValue)
  const [inputValue, setInputValue] = useState(cleanInitialValue)
  const [errors, setErrors] = useState(null)
  const lastValidValue = useRef(inputValue)
  const timeoutIdRef = useRef(null)

  const validateInput = useCallback(value => {
    if (validate) {
      const { errors = null } = validate(value)
      setErrors(errors)
      return !errors
    }
    return true
  }, [validate])

  // updates the visual component
  // stores last change as valid only if it's valid
  const onChange = e => {
    const value = e.target.value
    setInputValue(value)

    if (validateInput(value)) {
      lastValidValue.current = value
    }
    // if min or max exist, clamp the attempted value
    else if (isNumber(min) && Number(value) < Number(min)) {
      lastValidValue.current = min
    }
    else if (isNumber(max) && Number(value) > Number(max)) {
      lastValidValue.current = max
    }
  }

  useEffect(() => {
    if (cleanInitialValue != inputValue && validateInput(cleanInitialValue)) {
      setInputValue(cleanInitialValue)
      lastValidValue.current = cleanInitialValue 
    }
  }, [cleanInitialValue])

  const onBlur = () => {
    if (!errors && cleanInitialValue !== lastValidValue.current) {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      update(lastValidValue.current)
    } else {
      setInputValue(lastValidValue.current)
      setErrors(null)
    }
  }

  const onKeyUp = ({ key }) => {
    if (key === 'Enter' && !errors && cleanInitialValue !== lastValidValue.current) {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      update(lastValidValue.current)
    }
  }

  useEffect(() => {
    if (!errors && cleanInitialValue !== lastValidValue.current) {
      timeoutIdRef.current = setTimeout(() => {
        update(lastValidValue.current)
      }, delay)
    }
    return () => clearTimeout(timeoutIdRef.current)
  }, [inputValue, errors, delay, cleanInitialValue])

  return {
    value: inputValue ?? '',
    errors,
    onChange,
    onBlur,
    onKeyUp,
  }
}
